import { safeRuleLabel } from "../fixture-placement-rules.js";
import { isFirstPartyApp } from "../first-party-apps.js";
import { ROOT_SHELL_APPS } from "./root-shell-apps.js";
import { readSurfaceRows } from "./surface-rows.js";
import { createAssignabilityChecker } from "./surface-assignability.js";
//#region src/surfaces/check-surface-uses.ts
/**
* Every `uses[]` row holds against the surfaces it names, in both directions, against every
* pinned version (layered shell surfaces, D20, D21, R-6, R-20 Q10, Q12).
*
* An embedder's `uses[]` row says which embedded surface it mounts, the input it passes, and the
* outputs it reads. A publish can break that pair from either side: the embedder can start passing
* an input the target refuses, or the target can drop an output field an embedder reads. The
* publish door holds both directions against what is pinned now, so a refusal names the version,
* the channel and the ring where the broken pair would run (R-6). The relation is
* `findAssignabilityMismatch`; this module decides which pairs to compare, and how hard a break of
* a pinned consumer refuses (R-21).
*/
function issue(rule, path, message, severity = "error") {
	return {
		severity,
		path,
		rule,
		message
	};
}
/**
* True when a consumer's break refuses the producer's publish: the consumer is a root shell or a
* first-party app (R-21). `app` is the counterpart's registered slug, which WS-4 reads from
* `release.apps`, never an `appId` a bundle writes.
*/
function isPlatformConsumer(app) {
	return ROOT_SHELL_APPS.includes(app) || isFirstPartyApp(app);
}
function byCodeUnit(a, b) {
	return a < b ? -1 : a > b ? 1 : 0;
}
function compareCounterparts(a, b) {
	return byCodeUnit(a.app, b.app) || byCodeUnit(a.version, b.version) || byCodeUnit(a.channelId, b.channelId) || byCodeUnit(a.ring, b.ring);
}
/**
* The counterparts grouped by `(app, version)`, sorted by app then version by code unit. A version
* pinned on several rings is judged once and named with every pin. The first row of a group, in
* sort order, supplies the surfaces.
*/
function counterpartVersions(counterparts) {
	const groups = [];
	for (const row of [...counterparts].sort(compareCounterparts)) {
		const last = groups[groups.length - 1];
		const pin = `${safeRuleLabel(row.channelId)}/${safeRuleLabel(row.ring)}`;
		if (last !== void 0 && last.first.app === row.app && last.first.version === row.version) {
			if (!last.pins.includes(pin)) last.pins.push(pin);
		} else groups.push({
			first: row,
			pins: [pin]
		});
	}
	return groups.map(({ first, pins }) => {
		const rows = readSurfaceRows({ surfaces: first.surfaces });
		return {
			app: first.app,
			version: first.version,
			label: `${safeRuleLabel(first.app)}@${safeRuleLabel(first.version)}, pinned on ${pins.join(", ")}`,
			rows,
			byName: rowsByName(rows)
		};
	});
}
/** The first row with each name. */
function rowsByName(rows) {
	const map = /* @__PURE__ */ new Map();
	for (const row of rows) if (row.name !== null && !map.has(row.name)) map.set(row.name, row);
	return map;
}
/**
* Steps 3 to 5 of the pair check: the target is embedded, it accepts the input the row passes,
* and it emits every output the row reads with at least the fields the row reads. `null` when the
* pair holds.
*/
function pairFault(target, use, fits) {
	const name = `surface '${safeRuleLabel(target.name)}'`;
	if (target.claimsRoot || target.kind !== "embedded") return {
		step: "not-embedded",
		detail: `${name} ${target.claimsRoot ? "is the root surface" : "is not embedded"}; uses[] names embedded surfaces only (R-20 Q10).`
	};
	if (use.inputs !== void 0 && !target.hasInput) return {
		step: "input",
		detail: `${name} takes no input, and the uses[] row passes one (D21).`
	};
	const input = fits(use.inputs, target.input);
	if (input !== null) return {
		step: "input",
		detail: `${name} input does not accept what the uses[] row passes: ${input.reason} at '${safeRuleLabel(input.path)}' (D21).`
	};
	for (const [output, reader] of use.outputs) {
		const outputName = `'${safeRuleLabel(output)}'`;
		if (!target.outputs.has(output)) return {
			step: "output",
			detail: `${name} emits no output ${outputName}, which the uses[] row reads (D21).`
		};
		const found = fits(target.outputs.get(output), reader);
		if (found !== null) return {
			step: "output",
			detail: `${name} output ${outputName} does not give what the uses[] row reads: ${found.reason} at '${safeRuleLabel(found.path)}' (D21).`
		};
	}
	return null;
}
const STEP_RULE = {
	"not-embedded": "surface-uses-not-embedded",
	input: "surface-uses-input-unsatisfied",
	output: "surface-uses-output-unsatisfied"
};
/**
* The owner part of a point id: the text before its first `/`; `null` when the text has none, so
* a malformed id names no owner (surface extension points, D2).
*/
function pointOwner(id) {
	const slash = id.indexOf("/");
	return slash < 0 ? null : id.slice(0, slash);
}
const NO_TARGETS = Object.freeze([]);
/**
* Each string entry of a named row's `implements`, mapped to the names of the rows that list it,
* in row order, each name once. A nameless row implements nothing a mount can name.
*/
function implementersByPoint(rows) {
	const map = /* @__PURE__ */ new Map();
	for (const row of rows) {
		if (row.name === null) continue;
		for (const entry of row.implements) {
			if (typeof entry !== "string") continue;
			const names = map.get(entry);
			if (names === void 0) map.set(entry, [row.name]);
			else if (!names.includes(row.name)) names.push(row.name);
		}
	}
	return map;
}
/**
* The app whose implementer a point use row mounts (surface extension points, D9): the app it
* names in `app`, or the point owner when it names none. `null` for a row with no string `point`,
* and for a malformed id with no `app`, so such a row mounts no app's surface.
*/
function pointApp(use) {
	if (use.point === null) return null;
	return use.app ?? pointOwner(use.point);
}
/**
* The surfaces of this bundle that a point use row can mount (surface extension points, D9). The
* row mounts an implementer of `pointApp(use)`. When that app is `appSlug`, every row of this
* bundle that lists the point in `implements` is a target, whatever its kind: the runtime picks
* among the implementers the viewer may see, so any of them can be the one it mounts (refuse over
* accept, F3 of the surface extension points index). Another app, a malformed id with no `app`, or
* a non-string `point` gives no target.
*/
function pointTargets(use, appSlug, byPoint) {
	return pointApp(use) === appSlug ? byPoint.get(use.point) ?? NO_TARGETS : NO_TARGETS;
}
/** True when the row is among the targets of its own point use row, so the mount embeds itself. */
function embedsItself(row, targets) {
	return row.name !== null && targets.includes(row.name);
}
/** Pass A for one point use row whose target is this row itself: `surface-uses-self` (D9, D21). */
function pointSelfIssue(row, use, targets) {
	if (!embedsItself(row, targets)) return null;
	return issue("surface-uses-self", use.pointer, `surface '${safeRuleLabel(row.name)}' names point '${safeRuleLabel(use.point)}' in uses[] and implements it in this app, so the mount embeds itself; a surface cannot embed itself (D9, D21).`);
}
/** Pass A for one use row naming this app: judged against this bundle. */
function sameAppIssue(row, use, own, fits) {
	const surface = use.surface;
	if (surface === row.name) return issue("surface-uses-self", use.pointer, `surface '${safeRuleLabel(surface)}' lists itself in uses[]; a surface cannot embed itself (D21).`);
	const target = own.get(surface);
	if (target === void 0) return issue("surface-uses-surface-unknown", use.pointer, `uses[] names surface '${safeRuleLabel(surface)}' of this app, which this version does not declare (D21).`);
	const fault = pairFault(target, use, fits);
	return fault === null ? null : issue(STEP_RULE[fault.step], use.pointer, `in this version: ${fault.detail}`);
}
/** Pass A for one use row naming another app: judged against every pinned version of it. */
function crossAppIssues(use, versions, registered, fits) {
	const app = use.app;
	const surface = use.surface;
	if (registered !== null && !registered.has(app)) return [issue("surface-uses-app-unknown", use.pointer, `uses[] names app '${safeRuleLabel(app)}', which is not registered (D21).`)];
	const issues = [];
	for (const version of versions) {
		if (version.app !== app) continue;
		const target = version.byName.get(surface);
		if (target === void 0) {
			issues.push(issue("surface-uses-surface-unknown", use.pointer, `uses[] names surface '${safeRuleLabel(surface)}', which ${version.label} does not declare (D21).`));
			continue;
		}
		const fault = pairFault(target, use, fits);
		if (fault !== null) issues.push(issue(STEP_RULE[fault.step], use.pointer, `against ${version.label}: ${fault.detail}`));
	}
	return issues;
}
/**
* The edges of one use row inside this bundle, in this order: the `{app, surface}` edge, then one
* edge per point target (`pointTargets`), in row order. A target that is the row itself forms no
* edge, and pass A reports it as `surface-uses-self`; the edges to the other targets of the same
* point row stay, so a cycle through another implementer is still found (R-002).
*/
function useEdges(row, use, appSlug, index, byPoint) {
	const edges = [];
	const to = use.app === appSlug && use.surface !== null && use.surface !== row.name ? index.get(use.surface) : void 0;
	if (to !== void 0) edges.push({
		to,
		use,
		target: use.surface,
		point: null
	});
	for (const target of pointTargets(use, appSlug, byPoint)) if (target !== row.name) edges.push({
		to: index.get(target),
		use,
		target,
		point: use.point
	});
	return edges;
}
/**
* The `surface-uses-cycle` issue for the back edge `edge` from the top of `stack`. The cycle path
* runs from the frame of `edge.to` to the top, then along `edge`. The message names every point
* id on that path once, in path order, so a cycle through a point reads as one whichever edge
* closes it (R-002). The path holds at most one edge per row.
*/
function cycleAt(rows, stack, edge) {
	const from = stack.findIndex((frame) => frame.node === edge.to);
	const path = [...stack.slice(from + 1).map((frame) => frame.entry), edge.point];
	const points = [...new Set(path.filter((point) => point !== null))];
	const through = edge.point === null ? "" : ` through point '${safeRuleLabel(edge.point)}'`;
	const named = points.length === 0 ? "" : `; the cycle passes through point ${points.map((point) => `'${safeRuleLabel(point)}'`).join(", ")}`;
	return issue("surface-uses-cycle", edge.use.pointer, `surface '${safeRuleLabel(rows[stack[stack.length - 1].node].name)}' embeds '${safeRuleLabel(edge.target)}'${through}, which already embeds it through this app's uses[] rows${named}; an embedded cycle recurses at mount (D21).`);
}
/**
* The first back edge of a depth-first walk over this app's own `uses[]` edges, `{app, surface}`
* rows and point rows alike, in row order and use order. Iterative, so a large manifest cannot
* exhaust the stack.
*/
function cycleIssue(rows, appSlug, byPoint) {
	const index = /* @__PURE__ */ new Map();
	rows.forEach((row, i) => {
		if (row.name !== null && !index.has(row.name)) index.set(row.name, i);
	});
	const edges = rows.map((row) => row.uses.flatMap((use) => useEdges(row, use, appSlug, index, byPoint)));
	const state = rows.map(() => 0);
	for (let start = 0; start < rows.length; start += 1) {
		if (state[start] !== 0) continue;
		state[start] = 1;
		const stack = [{
			node: start,
			next: 0,
			entry: null
		}];
		while (stack.length > 0) {
			const top = stack[stack.length - 1];
			const edge = edges[top.node][top.next];
			if (edge === void 0) {
				state[top.node] = 2;
				stack.pop();
				continue;
			}
			top.next += 1;
			if (state[edge.to] === 1) return cycleAt(rows, stack, edge);
			if (state[edge.to] === 0) {
				state[edge.to] = 1;
				stack.push({
					node: edge.to,
					next: 0,
					entry: edge.point
				});
			}
		}
	}
	return null;
}
/**
* Pass B for one pinned point use row: the row mounts an implementer of this app
* (`pointApp(use)` is `appSlug`), and no row of this version lists the point in `implements`, so
* the pinned consumer would mount nothing (surface extension points, D9, D21). `null` otherwise.
*/
function pinnedPointIssue(use, where, appSlug, byPoint, severity) {
	if (pointApp(use) !== appSlug || byPoint.has(use.point)) return null;
	return issue("surface-breaks-pinned-consumer", "/surfaces", `this version implements no surface of point '${safeRuleLabel(use.point)}', which ${where()} mounts through uses[] (D9, D21).`, severity);
}
/**
* Pass B: every pinned embedder of this app, judged against this version; `error` for a platform
* consumer, `warning` for any other (R-21). An `{app, surface}` row runs steps 3 to 5 against the
* surface it names. A point row that mounts an implementer of this app needs at least one
* implementer of its point in this version (`pinnedPointIssue`).
*/
function pinnedConsumerIssues(own, versions, appSlug, fits, byPoint) {
	const issues = [];
	for (const version of versions) {
		if (version.app === appSlug) continue;
		const severity = isPlatformConsumer(version.app) ? "error" : "warning";
		for (const embedder of version.rows) {
			const where = () => `${version.label}, surface '${safeRuleLabel(embedder.name)}'`;
			for (const use of embedder.uses) {
				const dropped = pinnedPointIssue(use, where, appSlug, byPoint, severity);
				if (dropped !== null) issues.push(dropped);
				if (use.app !== appSlug || use.surface === null) continue;
				const target = own.get(use.surface);
				if (target === void 0) {
					issues.push(issue("surface-breaks-pinned-consumer", "/surfaces", `this version drops surface '${safeRuleLabel(use.surface)}', which ${where()} embeds (D21).`, severity));
					continue;
				}
				const fault = pairFault(target, use, fits);
				if (fault !== null) issues.push(issue("surface-breaks-pinned-consumer", target.pointer, `this version breaks ${where()}: ${fault.detail}`, severity));
			}
		}
	}
	return issues;
}
/**
* The `uses[]` issues of an untrusted manifest against its pinned counterparts (R-6).
*
* `ctx.appSlug` is the registered slug of the app being published. `ctx.counterparts` is every
* pinned version of each app this bundle's `uses[]` rows name, and of each app whose pinned rows
* name `ctx.appSlug`, in any order; rows of `ctx.appSlug` itself are ignored, since one app
* version ships all its surfaces together. `ctx.registeredAppSlugs` is `null` at the CLI, which
* skips `surface-uses-app-unknown`.
*
* Pass A treats this bundle as the embedder. For each use row with a string `app` and `surface`,
* in row order:
* 1. `app` equal to `ctx.appSlug`: the row names its own surface (`surface-uses-self`), a
*    surface this bundle does not declare (`surface-uses-surface-unknown`), or a target judged by
*    steps 3 to 5 against this bundle.
* 2. Another app: absent from a non-null `registeredAppSlugs` gives `surface-uses-app-unknown`.
*    Otherwise each pinned version, by app then version, is judged once: a version that lacks the
*    surface gives `surface-uses-surface-unknown`, and steps 3 to 5 run against the one that has
*    it. With no version pinned, the row breaks nothing.
* 3. A target that claims root or is not `embedded` gives `surface-uses-not-embedded`.
* 4. A row passing `inputs` to a target with no `input`, or `inputs` the target's `input` does not
*    accept, gives `surface-uses-input-unsatisfied`. An absent `inputs` fits a target that
*    requires nothing.
* 5. Per `outputs` name, a target that does not emit it, or emits less than the row reads, gives
*    `surface-uses-output-unsatisfied`.
* Each use row gets at most one issue per counterpart version, at `<use>`, naming
* `<app>@<version>` and every channel and ring it is pinned on, the surface, the reason and the
* reader path.
*
* A use row with a string `point` is the point arm (surface extension points, D9). It mounts an
* implementer of the app it names in `app`, or of the point owner (the id text before its `/`)
* when it names none. When that app is `ctx.appSlug`, every row of this bundle whose
* `implements` lists the point is a target, whatever its kind and whichever one the runtime would
* pick first. A row that is among its own targets gives `surface-uses-self` at `<use>`, as the
* same embed written as `{app, surface}` does. Pass B below judges a pinned consumer's point row.
* Every other check of a point row is `checkExtensionPoints`'s.
*
* Then the first back edge of a walk over this bundle's own edges gives `surface-uses-cycle` at
* the closing `<use>`. An edge runs from a row to the surface each of its same-app
* `{app, surface}` rows names, and to each target of each of its point rows, so a cycle that
* passes through a point is found as well. A target that is the row itself forms no edge, and the
* row's edges to the other implementers stay. The message names every point on the cycle path,
* once each, in path order (R-002).
*
* Pass B treats this bundle as the target. For each pinned version of another app, each of its
* use rows naming `ctx.appSlug` runs steps 3 to 5 against this bundle. A failure gives
* `surface-breaks-pinned-consumer` at this bundle's `<row>`, or at `/surfaces` when this bundle no
* longer declares the surface, naming the embedder's version, pins and surface. Each of its point
* rows that mounts an implementer of `ctx.appSlug` (the row's `app`, or the point owner when the
* row names no `app`) needs at least one row of this bundle whose `implements` lists the point,
* whatever that row's kind. With none, the row gives `surface-breaks-pinned-consumer` at
* `/surfaces`, naming the point and the embedder's version, pins and surface: the pinned consumer
* would mount nothing (WS-1-R6). Pass B runs no pair check on a point row: the point carries the
* pair, `checkExtensionPoints` judges each side against it, and D5 freezes the point. A
* counterpart whose `surfaces` is `[]` (none declared, or an unreadable manifest) gives pass A's
* unknown-surface issue and nothing in pass B.
*
* Severity. Every issue is `severity: 'error'` except one class: a pass-B issue whose embedder is
* neither on `ROOT_SHELL_APPS` nor a first-party app (`isFirstPartyApp`) is `severity: 'warning'`.
* A third-party consumer cannot block another app's release; the door records the warning and the
* consumer degrades at mount through the tolerant check (R-21, D8). The membership is read from
* the counterpart's registered slug.
*
* Cost. One assignability checker serves the whole call, so each distinct schema object is walked
* for representability at most once (at most 513 nodes) and each union node is indexed once; each
* pinned version's rows are read and indexed by name once. A comparison then costs at most the
* reader's node count. In pass A that is at most 512 steps per own use row and pinned version for
* the input, and the row's own `outputs` readers once per pinned version; in pass B, at most 512
* steps for each consumer row's input and the consumer's own readers for its outputs. So the work
* grows with the bytes of the manifest and of the counterparts times the pinned versions per app,
* and never with use rows times counterpart schema size (security-2 of the S1 WS-1 review).
* The point edges are indexed once per call; a point row adds at most one edge per implementer
* of its point in this bundle, and the walk visits each edge once.
*
* Counterpart surfaces are read through the same guarded reader as the manifest, so a malformed
* stored row cannot throw. App-authored strings and pins reach a message only through
* `safeRuleLabel`. Pure; never throws.
*/
function checkSurfaceUses(appManifest, ctx) {
	const rows = readSurfaceRows(appManifest);
	const own = rowsByName(rows);
	const versions = counterpartVersions(ctx.counterparts);
	const fits = createAssignabilityChecker();
	const byPoint = implementersByPoint(rows);
	const issues = [];
	for (const row of rows) for (const use of row.uses) {
		const self = pointSelfIssue(row, use, pointTargets(use, ctx.appSlug, byPoint));
		if (self !== null) issues.push(self);
		if (use.app === null || use.surface === null) continue;
		if (use.app === ctx.appSlug) {
			const found = sameAppIssue(row, use, own, fits);
			if (found !== null) issues.push(found);
		} else issues.push(...crossAppIssues(use, versions, ctx.registeredAppSlugs, fits));
	}
	const cycle = cycleIssue(rows, ctx.appSlug, byPoint);
	if (cycle !== null) issues.push(cycle);
	issues.push(...pinnedConsumerIssues(own, versions, ctx.appSlug, fits, byPoint));
	return issues;
}
//#endregion
export { checkSurfaceUses };

//# sourceMappingURL=check-surface-uses.js.map